import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import getAnnouncementInitialValues from "@/data/ContentManagement/Announcements/initial-values";
import {
  announcementsApi,
  sportsMasterDataApi,
  announcementPagesApi,
} from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import AnnouncementSchema from "@/schema/ContentManagement/Announcements/AnnouncementSchema";
import LocalizedInput from "@/components/LocalizedInput";
import SelectInput from "@/components/SelectInput/SelectInput";
import { useSelector } from "react-redux";
import { booleanOptionFormOptions } from "@/constant/GeneralOptions/boolean";
import { announcementTypeValues } from "@/data/ContentManagement/Announcements/type-values";
import RadioInput from "@/components/RadioInput/Index";

const AnnouncementModal = forwardRef((_, ref) => {
  const booleanOptionFormOptionsMap = booleanOptionFormOptions();
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id ? `${announcementsApi}/${modalState.id}` : null;

  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const defaultPageValueFormatter = useCallback((item) => item?.slug, []);

  const localeCodes = useMemo(() => {
    return locales ? locales.map((locale) => locale.code) : [];
  }, [locales]);

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Announcement ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${announcementsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: announcementsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getAnnouncementInitialValues(
      localeCodes,
      modalState.id ? showData : null
    ),
    validationSchema: AnnouncementSchema(localeCodes, !!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData: !!modalState?.id && showData?.data?.sport,
  });
  const allSportOption = [
    { label: "SP-All", value: "all" },
    ...sportDefaultOptions,
  ];

  const announcemetPageListApi = `${announcementPagesApi}${baseQueryParams}`;
  const {
    loading: announcementPageLoading,
    defaultOptions: announcemetPageDefaultOptions,
    debouncedFetchOptions: announcementPageFetchOptions,
    getSelectedOption: announcementPageGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: announcemetPageListApi,
    formikValue: formik.values.page,
    formikInitialValue: formik.initialValues.page,
    showData: !!modalState?.id && showData?.data?.page,
    valueFormatter: defaultPageValueFormatter,
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) {
      formData.append("_method", "PUT");
    }
    formData.append("text", values.text);
    formData.append("name", values.name);
    formData.append("is_closable", values.is_closable);
    formData.append("page", values.page);
    formData.append("type", values.type);
    formData.append("sport_id", values.sport_id);
    Object.keys(values.localizations).forEach((key) => {
      if (values.localizations[key]) {
        formData.append(`localizations[${key}]`, values.localizations[key]);
      }
    });
    formData.append("status", values.status);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Announcement" : "Add Announcement"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"name"}
            saveError={saveError}
            label="Name"
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <SelectInput
            label="Type"
            placeholder="No type selected."
            defaultOptions={announcementTypeValues}
            value={
              announcementTypeValues.find(
                (option) => option.value === formik.values.type
              ) || null
            }
            onChange={(selected) =>
              formik.setFieldValue("type", selected.value)
            }
            errorText={
              (formik.touched.type && formik.errors.type) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.type?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Page"
            placeholder="No page selected. Type to search..."
            defaultOptions={announcemetPageDefaultOptions}
            loadOptions={announcementPageFetchOptions}
            isLoading={announcementPageLoading}
            value={announcementPageGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("page", selected?.value);
            }}
            errorText={
              (formik.touched.page && formik.errors.page) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.page?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="Sport"
            placeholder="No sport selected. Type to search..."
            defaultOptions={allSportOption}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={
              allSportOption.find(
                (option) => option.value === formik.values.sport_id
              ) || null
            }
            onChange={(selected) => {
              formik.setFieldValue("sport_id", selected?.value || "all");
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName="is_closable"
            label="Is Closable"
          />
        </Col>
      </Row>
      <LocalizedInput
        formik={formik}
        error={saveError}
        isAnnouncementsPage={true}
        isEdit={!!modalState.id}
      />
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default AnnouncementModal;
